import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams} from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { useTranslation } from 'react-i18next'; 
import { ISupplierData } from "../../../interfaces/ISupplierData";
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateSupplier: React.FunctionComponent<IPageProps> = () => {
  const { supplierId } = useParams<any>();
  const toast = useRef<any>(null);
  const [supplierData, setSupplierData] = useState<ISupplierData | null>(null);
  const [companyData, setCompanyData] = useState<ICompanyData[]>([]);
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCompanyData();
    if (supplierId) {
      fetchSupplierData(supplierId);
    }
  }, [supplierId]);

  useEffect(() => {
    if (supplierId!=null) {
      fetchSupplierData(supplierId);
    } else if (supplierData?.supplierId) {
      fetchSupplierData(supplierData.supplierId);
    }
  }, [supplierId, supplierData?.supplierId]);


  const fetchCompanyData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const fetchSupplierData = async (id: number) => {
    try {
      const res = await HTTPService.GET(`/Supplier/ById?supplierId=${id}`);
      if (res.status === 200) {
        setSupplierData(res.data);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: res.error.message,
          life: 3000
        });
      }
    } catch (error) {
      console.error("Error fetching supplier data:", error);
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('error_fetching_supplier'),
        life: 3000
      });
    }
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };
  

  const handleUpdateSupplier = async (formData: { supplierName: string; companyId?: string }) => {
    try {
      const updatedSupplier = {
        supplierId: supplierId,
        supplierName: formData.supplierName,
        companyId: formData.companyId || userInfo.companyId,
      };

      const res = await HTTPService.PUT(`/Supplier/updateSupplier`, updatedSupplier);
      if (res.status === 200) {
        toast.current?.show({
          severity: 'success',
          summary: t('successful'),
          detail: t('supplier_updated_successfully'),
          life: 3000
        });
        window.location.replace("/admin/supplier-management");
      } else if(res.status === 450) {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(res.data),
          life: 3000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(res.data),
          life: 3000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('error_updating_supplier'),
        life: 3000
      });
    }
  };

  const handleCancel = () => {
    window.location.replace("/admin/supplier-management/");
  };

  return (
    <>
      <Toast ref={toast} />
      <Form
        layout="vertical"
        onFinish={handleUpdateSupplier}
        fields={[
          { name: ["companyId"], value: supplierData?.companyId},
          { name: ["supplierName"], value:  supplierData?.supplierName},
        ]}
      >
        <Card style={{ padding: "50px" }}>
          <h3>{t('update_supplier')}</h3>
          {userInfo?.role === UserRole.AltisAdmin && (
            <Form.Item
              name='companyId'
              label={t('Select company')}
              rules={[
                {
                  required: true,
                  message: t('please_select_a_company'),
                },
              ]}
            >
              <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '16px' }} filterOption={filterOption}>
                {companyData.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            name="supplierName"
            label={t('supplier_name')}
            rules={[
              {
                required: true,
                message: t('please_input_your_supplier_name'),
                whitespace: true,
              },
            ]}
          >
            <Input
              style={{ borderRadius: 0 }}
              placeholder={t('supplier_name')}
            />
          </Form.Item>

          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
                <Col xs={12} sm={4}>
                    <Button
                        block
                        onClick={handleCancel}
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <CloseOutlined style={{ fontSize: 20 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('cancel')}
                        </span>
                    </Button>
                </Col>
                <Col xs={12} sm={4}>
                    <Button
                        block
                        htmlType="submit"
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <SyncOutlined style={{ fontSize: 20 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('update')}
                        </span>
                    </Button>
                </Col>
            </Row>
        </Form.Item>
        </Card>
      </Form>
    </>
  );
};

export default UpdateSupplier;
